/**
 * Admin metrics dashboard API layer (STORY-M1-DASH). Typed calls to the
 * backend admin metrics router (`GET /admin/metrics/overview?days=…`, gated
 * `ops.metrics.read`). Read-only: no mutating calls, no Idempotency-Key.
 *
 * Types are HAND-WRITTEN mirrors of the backend Pydantic DTOs in
 * `apps/api/garden/features/admin_metrics/schemas.py` (`MetricsOverview` +
 * every tile response). The BACKEND is the source of truth: field names and
 * nullability mirror the DTOs VERBATIM (snake_case wire names). All aggregates
 * are numbers (counts, milliseconds, 0..1 ratios); there is NO GMV / money
 * field anywhere in this payload.
 */
import { apiClient } from "@/lib/api-client";

// ─────────────────────────── Range → days ──────────────────────────────────

export const METRICS_RANGES = ["7d", "30d", "90d"] as const;
export type MetricsRange = (typeof METRICS_RANGES)[number];

const RANGE_TO_DAYS: Record<MetricsRange, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
};

/** Map a range button to the backend `days` window parameter. */
export function rangeToDays(range: MetricsRange): number {
  return RANGE_TO_DAYS[range];
}

// ─────────────────────────── Shared shapes (mirror BE) ─────────────────────

/** `TrendPoint` (BE) — one (UTC day, count) point on a daily series. */
export interface TrendPoint {
  date: string; // ISO date (YYYY-MM-DD)
  count: number;
}

/** `DemandSlice` (BE) — labelled bucket; also `LanguageSplit` + `rating_dist`. */
export interface DemandSlice {
  key: string;
  count: number;
}

/** `IntentSlice` (BE) — one classified-intent share of completed responses. */
export interface IntentSlice {
  intent: string;
  count: number;
}

// ─────────────────────────── Tile DTOs (mirror BE) ─────────────────────────

/** `ActiveUsersResponse` (BE-2). */
export interface ActiveUsers {
  dau: TrendPoint[];
  wau: number;
  mau: number;
}

/** `AuthFunnelResponse` (BE-3). */
export interface AuthFunnel {
  signup_started: number;
  signup_completed: number;
  login_success: number;
  login_failure: number;
  daily_signups: TrendPoint[];
}

/** `ChatUsageResponse` (BE-4). */
export interface ChatUsage {
  messages_total: number;
  threads_total: number;
  latency_p95_ms: number | null;
  error_rate: number; // 0..1
  intent_mix: IntentSlice[];
  daily_volume: TrendPoint[];
}

/** `MarketplaceFunnelResponse` (BE-5). */
export interface MarketplaceFunnel {
  searches: number;
  product_views: number;
  seller_views: number;
  contacts: number;
  directions: number;
}

/** `OocDemandResponse` (BE-6). */
export interface OocDemand {
  by_region: DemandSlice[];
  by_category: DemandSlice[];
  total: number;
}

/** `FeedbackMetricsResponse` (BE-7). */
export interface Feedback {
  open_count: number;
  replied_count: number;
  closed_count: number;
  avg_rating: number | null; // 1..5
  rating_dist: DemandSlice[];
  oldest_open_age_hours: number | null;
  sla_breaches: number;
}

/** `SellerOnboardingResponse` (BE-8). */
export interface SellerOnboarding {
  submitted: number;
  email_verified: number;
  phone_verified: number;
  photo_uploaded: number;
  approved: number;
  rejected: number;
  dropped_off: number;
}

/** `LanguageSplitResponse` (BE-9). */
export interface LanguageSplit {
  slices: DemandSlice[];
}

/** `NotificationMetricsResponse` (BE-10). */
export interface Notifications {
  created: number;
  read: number;
  read_rate: number; // 0..1
}

/** `OutboxQueueHealth` (BE-11) — one drain queue. */
export interface OutboxQueueHealth {
  queue: string;
  pending: number;
  sent: number;
  failed: number;
  max_attempts: number;
}

/** `OutboxHealthResponse` (BE-11). */
export interface OutboxHealth {
  rows: OutboxQueueHealth[];
}

/** `MetricsOverview` (BE-12) — all 10 tiles in one round trip. */
export interface MetricsOverview {
  active_users: ActiveUsers;
  auth_funnel: AuthFunnel;
  chat_usage: ChatUsage;
  marketplace_funnel: MarketplaceFunnel;
  ooc_demand: OocDemand;
  feedback: Feedback;
  seller_onboarding: SellerOnboarding;
  language_split: LanguageSplit;
  notifications: Notifications;
  outbox_health: OutboxHealth;
}

// ─────────────────────────── Query keys ────────────────────────────────────

export const metricsKeys = {
  overview: (days: number) =>
    ["admin", "metrics", "overview", days] as const,
} as const;

// ─────────────────────────── Fetcher ───────────────────────────────────────

export function fetchMetricsOverview(days: number): Promise<MetricsOverview> {
  const query = new URLSearchParams({ days: String(days) });
  return apiClient.get<MetricsOverview>(
    `/admin/metrics/overview?${query.toString()}`,
  );
}
